import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Hero.css';

function Hero() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);
  const heroRef = useRef(null);
  const statsAnimated = useRef(false);

  useEffect(() => {
    // Generate floating particles
    const particleCount = 25;
    const newParticles = Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 10 + 15,
      delay: Math.random() * 5
    }));
    setParticles(newParticles);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 40 - 20;
      const y = (e.clientY / window.innerHeight) * 40 - 20;
      setMousePosition({ x, y });
    };

    // Touch tracking for mobile devices
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        const x = (touch.clientX / window.innerWidth) * 40 - 20;
        const y = (touch.clientY / window.innerHeight) * 40 - 20;
        setMousePosition({ x, y });
      }
    };

    // Device orientation tracking for mobile
    const handleDeviceOrientation = (e) => {
      if (e.gamma !== null && e.beta !== null) {
        const x = (e.gamma / 90) * 30; // Max 30px movement
        const y = (e.beta / 90) * 30;
        setMousePosition({ x, y });
      }
    };

    // Check if device supports touch
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (isTouchDevice) {
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
      if (window.DeviceOrientationEvent) {
        window.addEventListener('deviceorientation', handleDeviceOrientation, { passive: true });
      }
    } else {
      window.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('deviceorientation', handleDeviceOrientation);
    };
  }, []);

  // Animated counter for stats - runs only once on page load
  const Counter = ({ end, duration = 2000, suffix = '', prefix = '', decimals = 0 }) => {
    const [count, setCount] = useState(0);
    const counterRef = useRef(null);
    const animationStarted = useRef(false);

    useEffect(() => {
      // Only animate if stats haven't been animated yet
      if (statsAnimated.current || animationStarted.current) {
        setCount(typeof end === 'string' ? parseFloat(end) : end);
        return;
      }

      animationStarted.current = true;
      statsAnimated.current = true;

      let startTime;
      const endValue = typeof end === 'string' ? parseFloat(end) : end;
      const isDecimal = endValue % 1 !== 0;

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;
        const percentage = Math.min(progress / duration, 1);
        const easeOut = 1 - Math.pow(1 - percentage, 3);

        let currentValue = endValue * easeOut;

        if (decimals > 0 || isDecimal) {
          setCount(parseFloat(currentValue.toFixed(decimals || 1)));
        } else {
          setCount(Math.floor(currentValue));
        }

        if (percentage < 1) {
          requestAnimationFrame(animate);
        } else {
          setCount(endValue);
        }
      };

      // Start animation after a short delay
      const timeout = setTimeout(() => {
        requestAnimationFrame(animate);
      }, 500);

      return () => clearTimeout(timeout);
    }, [end, duration, decimals]);

    const displayValue = decimals > 0 || (typeof end === 'number' && end % 1 !== 0)
      ? count.toFixed(decimals || 1)
      : count;

    return <span ref={counterRef}>{prefix}{displayValue}{suffix}</span>;
  };

  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="hero" ref={heroRef}>
      <div className="hero-background">
        {/* Animated Particles */}
        <div className="particles">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="particle"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                animationDuration: `${particle.duration}s`,
                animationDelay: `${particle.delay}s`
              }}
            />
          ))}
        </div>

        {/* Gradient Orbs */}
        <div
          className="gradient-orb orb-1"
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`
          }}
        ></div>
        <div
          className="gradient-orb orb-2"
          style={{
            transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)`
          }}
        ></div>
        <div
          className="gradient-orb orb-3"
          style={{
            transform: `translate(${mousePosition.x * 0.5}px, ${-mousePosition.y * 0.5}px)`
          }}
        ></div>

        {/* Animated Shapes */}
        <div className="floating-shapes">
          <div className="shape shape-circle"></div>
          <div className="shape shape-square"></div>
          <div className="shape shape-triangle"></div>
          <div className="shape shape-hexagon"></div>
        </div>

        {/* Grid Overlay */}
        <div className="grid-overlay"></div>

        {/* Animated Lines */}
        <svg className="animated-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path className="line line-1" d="M0,20 Q50,10 100,20" fill="none" />
          <path className="line line-2" d="M0,50 Q50,40 100,50" fill="none" />
          <path className="line line-3" d="M0,80 Q50,70 100,80" fill="none" />
        </svg>
      </div>

      <div className="container">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            <span className="badge-text">Start Your Ecommerce Business:</span>
          </div>

          <h1 className="hero-title">
            <span className="title-main">
              <span className="word word-1">Start</span>
              <span className="word word-2">Selling</span>
              <span className="word word-3">Online</span>
              <span className="word word-4">Today</span>
            </span>
          </h1>

          <p className="hero-description">
            We build custom Shopify stores that drive revenue, maximize retention, and turn your e-commerce vision into high-converting growth.
          </p>

          <div className="hero-cta">
            <button className="btn-primary btn-large magnetic" onClick={scrollToProjects}>
              <span className="btn-text">View Our Work</span>
              <span className="btn-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </button>
            <Link to="/get-started" className="btn-secondary btn-large magnetic">
              <span className="btn-text">Get Started</span>
            </Link>
          </div>

          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">
                <Counter end={50} suffix="+" />
              </div>
              <div className="stat-label">Stores Launched</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">
                <Counter end={5} prefix="$" suffix="M+" />
              </div>
              <div className="stat-label">Revenue Generated</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">
                <Counter end={2.5} suffix="x" decimals={1} />
              </div>
              <div className="stat-label">Avg. Conversion Increase</div>
            </div>
          </div>
        </div>
      </div>

      <div className="scroll-indicator">
        <div className="scroll-line"></div>
        <span>Scroll</span>
      </div>
    </section>
  );
}

export default Hero;
